import * as React from "react";
import { useEffect, useState } from "react";
import {
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { DataResult, State, translateDataSourceResultGroups } from "@progress/kendo-data-query";
import { Grid, useTheme } from "@mui/material";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "react-query";
import { NumericTextBoxPropsContext } from "@progress/kendo-react-inputs";
import { ContainerOverviewGridColumns } from "./ContainerOverviewGridColumns";
import useContainerOverviewGridQuery, {
  ContainerOverviewGridQueryKey,
} from "./Hooks/useContainerOverviewGridQuery";
import HasGroups from "../../../Grid/HasGroups";
import GridLoadingPanel from "../../../Grid/GridLoadingPanel";
import StyledKendoGrid from "../../../Grid/StyledKendoGrid";
import { InitialState } from "../../../Grid/InitialDataState";
import { OnRowClickType } from "../../../Layout/Grid/GridRowClickProps";
import { ValidationItemUpdateMode } from "../ValidationItemUpdateMode";
import { PdsrContainer } from "./PdsrContainer";
import { GridSelectableRowsProps } from "../../../Grid/GridSelectableRowsProps";
import { UppercaseButton } from "../../../Layout/Buttons/UppercaseButton";
import { KENDO_BUTTON_CLASSES } from "../../../Layout/Buttons/KendoButtons";
import { IconWithRightMargin } from "../../../Layout/Icons/IconWithRightMargin";
import { usePdsrToolbarActions } from "./Hooks/usePdsrToolbarActions";
import { DATA_ITEM_KEY, EDIT_FIELD, idGetter, SELECTED_FIELD } from "../../../Grid/GridConstants";
import StatePersistenceButtons from "../../../Grid/StatePersistenceButtons";
import useSavedState from "../../../Grid/UseSavedState";
import InlineEditProps from "../../../Grid/InlineEdit/InlineEditProps";
import updateItem from "../../../Grid/InlineEdit/UpdateItem";
import { combinedUrls } from "../../../URL/functions";
import { pdsrUrls } from "../../../URL/ApplicationUrls";
import { useDefaultNumericTextBoxProps } from "../../../Grid/useDefaultNumericTextBoxProps";
import { useIsUserInRoles } from "../../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";

type Props = {
  pdsrId?: string;
};

const CONTAINEROVERVIEWGRID_DATASTATE = "ContainerOverviewGrid_DataState";

export const ContainerOverviewGrid = ({ pdsrId }: Props) => {
  // Data
  const [dataResult, setDataResult] = useState<DataResult>({
    data: [],
    total: 0,
  });

  const onDataReceived = (newDataResult: DataResult) => {
    setDataResult(newDataResult);
    setSelectedContainerState({});
  };

  const [dataState, setDataState] = useSavedState<State>(
    CONTAINEROVERVIEWGRID_DATASTATE,
    InitialState
  );
  const { data: containerData, isLoading } = useContainerOverviewGridQuery({
    pdsrId,
    dataState,
  });

  useEffect(() => {
    if (containerData) {
      onDataReceived.call(undefined, {
        data: HasGroups(dataState)
          ? translateDataSourceResultGroups(containerData.Data)
          : containerData.Data,
        total: containerData.Total,
      });
    }
    setGridSelectionEmpty();
  }, [containerData]);

  // Selection
  const [selectedContainerState, setSelectedContainerState] = React.useState<{
    [id: number]: boolean | number[];
  }>({});
  const [selectedContainerIds, setSelectedContainerIds] = useState<Array<number>>([]);

  const selectableContainerRowsProps = GridSelectableRowsProps<PdsrContainer>({
    selectedState: selectedContainerState,
    setSelectedState: setSelectedContainerState,
    setSelectedIds: setSelectedContainerIds,
    dataItemKey: DATA_ITEM_KEY,
  });

  const onRowClick: OnRowClickType = (dataItem: PdsrContainer) => {
    const id = dataItem[DATA_ITEM_KEY];
    if (!selectedContainerState[id]) {
      setSelectedContainerState({ [id]: true });
      setSelectedContainerIds([...selectedContainerIds, id]);
    }
  };

  // Toolbar Actions
  const { expireContainer, deleteContainer } = usePdsrToolbarActions();

  const getDeletableContainers = (selectedIds: Array<number>) => {
    const deletableContainers: Array<number> = [];
    const containers: Array<PdsrContainer> = containerData?.Data;

    if (!containers || !containers.length) {
      return deletableContainers;
    }
    containers?.forEach((container: PdsrContainer) => {
      if (
        selectedIds.includes(container.Id) &&
        (container.UpdateMode === ValidationItemUpdateMode.Expired ||
          container.UpdateMode === ValidationItemUpdateMode.ExpiredManual)
      ) {
        deletableContainers.push(container.Id);
      }
    });
    return deletableContainers;
  };

  const deletableContainerIds = getDeletableContainers(selectedContainerIds);

  const queryClient = useQueryClient();

  const setGridSelectionEmpty = () => {
    // this is a temp workout; invalidateQueries causes toast to disappear immediately
    // todo fix me
    setTimeout(() => {
      queryClient.invalidateQueries([ContainerOverviewGridQueryKey]);
    }, 2_000);
    setSelectedContainerIds([]);
  };

  const [editState, setEditState] = useState<number>();
  const inlineEditProps = InlineEditProps({
    idField: DATA_ITEM_KEY,
    dataResult,
    onDataReceived,
    setEditState,
    saveChanges: (changedItem) => {
      return updateItem(
        combinedUrls(pdsrUrls.containersForPdsrWithId(pdsrId), idGetter(changedItem)),
        changedItem
      );
    },
    isReadonly: false,
  });

  const numericTextBoxProps = useDefaultNumericTextBoxProps();

  const theme = useTheme();
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);
  return (
    <Grid container columnSpacing={1}>
      <Grid item xs={12}>
        <NumericTextBoxPropsContext.Provider value={numericTextBoxProps}>
          <StyledKendoGrid
            style={{ marginTop: "2rem" }}
            muiTheme={theme}
            data={{
              data: dataResult.data.map((item) => ({
                ...item,
                [EDIT_FIELD]: item[DATA_ITEM_KEY] === editState,
                [SELECTED_FIELD]: selectedContainerState[idGetter(item)],
              })),
              total: dataResult.total,
            }}
            sortable
            pageable
            resizable
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
            {...dataState}
            {...inlineEditProps}
            {...selectableContainerRowsProps}
          >
            <GridToolbar>
              <StatePersistenceButtons
                localStorageKey={CONTAINEROVERVIEWGRID_DATASTATE}
                initialState={InitialState}
                dataState={dataState}
                resetButtonText="RESET FILTER"
                saveButtonText="SAVE FILTER"
                align="left"
              />
              <UppercaseButton
                className={KENDO_BUTTON_CLASSES}
                disabled={selectedContainerIds.length === 0 || !isUserInAdminNetworkRole}
                onClick={() => {
                  expireContainer(pdsrId, selectedContainerIds);
                  setGridSelectionEmpty();
                }}
              >
                <IconWithRightMargin icon={faXmark} />
                Expire Container
              </UppercaseButton>
              <UppercaseButton
                className={KENDO_BUTTON_CLASSES}
                disabled={selectedContainerIds.length === 0 || !isUserInAdminNetworkRole}
                onClick={() => {
                  deleteContainer(pdsrId, deletableContainerIds);
                  setGridSelectionEmpty();
                }}
              >
                <IconWithRightMargin icon={faXmark} />
                Delete Container
              </UppercaseButton>
            </GridToolbar>
            {[
              <Column
                key="SelectHeader"
                field={SELECTED_FIELD}
                width="50px"
                headerSelectionValue={
                  dataResult.data.findIndex((item) => !selectedContainerState[idGetter(item)]) ===
                  -1
                }
              />,
              ...ContainerOverviewGridColumns({
                onRowClick,
                isReadOnly: !isUserInAdminNetworkRole,
              }),
            ]}
          </StyledKendoGrid>
        </NumericTextBoxPropsContext.Provider>
        <GridLoadingPanel isLoading={isLoading} />
      </Grid>
    </Grid>
  );
};
